import { Beneficio, CategoriaMeta, GrupoMeta, Plan } from '../types/Plan';

/**
 * Planes vigentes de Gappex.
 * Fuente principal: TyC de Uso, última actualización Agosto 2026, cláusula 6°.
 * Excepciones: el descuento en farmacias adheridas sale del catálogo de Salesforce y
 * el de kinesiología / fisioterapia del acuerdo comercial vigente con la red.
 * Ante cualquier diferencia prevalece el TyC publicado en gappex.com.ar.
 */

export const TYC_URL = 'https://gappex.com.ar/terminos-y-condiciones/';
export const TYC_VERSION = 'Agosto 2026';

export const GRUPOS: GrupoMeta[] = [
  { id: 'Ambulatorio', alcance: 'Consultas y estudios — adultos y pediátrico' },
  { id: 'Internación y cirugía', alcance: 'Sólo adultos — no aplica a pediátrico' },
];

export const CATEGORIAS: CategoriaMeta[] = [
  { id: 'consultas', label: 'Consultas médicas', grupo: 'Ambulatorio' },
  { id: 'practicas-consultorio', label: 'Prácticas en consultorio', grupo: 'Ambulatorio' },
  { id: 'laboratorio', label: 'Laboratorio', sublabel: 'Análisis clínicos', grupo: 'Ambulatorio' },
  { id: 'imagenes', label: 'Imágenes diagnósticas', sublabel: 'RX, ECO, TAC, RMN y otros', grupo: 'Ambulatorio' },
  { id: 'kinesiologia', label: 'Kinesiología / Fisioterapia', sublabel: 'Sesiones ambulatorias de rehabilitación', grupo: 'Ambulatorio' },
  { id: 'guardia', label: 'Urgencias / Guardia', grupo: 'Ambulatorio' },
  { id: 'interconsultas', label: 'Interconsultas especializadas', grupo: 'Ambulatorio' },
  { id: 'farmacia', label: 'Medicamentos en farmacia', sublabel: 'Farmacias adheridas a la red', grupo: 'Ambulatorio' },

  { id: 'habitacion', label: 'Habitación / Terapia intensiva', grupo: 'Internación y cirugía' },
  { id: 'derechos-quirurgicos', label: 'Derechos quirúrgicos', grupo: 'Internación y cirugía' },
  { id: 'medicamentos-internacion', label: 'Medicamentos en internación', sublabel: 'Farmacia interna del prestador', grupo: 'Internación y cirugía' },
  { id: 'honorarios-cirujano', label: 'Honorarios cirujano', sublabel: 'Sólo profesionales con convenio', grupo: 'Internación y cirugía' },
  { id: 'honorarios-anestesista', label: 'Honorarios anestesista', sublabel: 'Sólo profesionales con convenio', grupo: 'Internación y cirugía' },
];

// --- Constructores de beneficio, para que la tabla de datos se lea de un vistazo ---

const si = (pct: number, resumen: string, detalle?: string): Beneficio => ({
  estado: 'si', pct, resumen, detalle,
});
const hasta = (pct: number, resumen: string, detalle?: string): Beneficio => ({
  estado: 'si', pct, esTope: true, resumen, detalle,
});
const no = (detalle?: string): Beneficio => ({
  estado: 'no', pct: null, resumen: 'Sin descuento', detalle,
});
const nd = (detalle?: string): Beneficio => ({
  estado: 'no-especificado', pct: null, resumen: 'No aplica', detalle,
});

// --- Textos que se repiten ---

const SIN_DESCUENTOS = 'Este plan no otorga descuentos sobre prestaciones médicas.';
const NO_PREVISTO =
  'El TyC no contempla esta prestación para este plan. Los beneficios se interpretan con criterio restrictivo (cláusula 1°), así que no corresponde informar descuento.';
const GUARDIA_EXCLUIDA = 'El TyC excluye expresamente el descuento sobre atenciones de urgencia o guardia.';
const INTERCONSULTA_EXCLUIDA = 'El TyC excluye expresamente la interconsulta especializada.';
const CENTROS_CONVENIO =
  'Aplica sólo en centros de salud incluidos en la lista de "centros de salud con convenio y acuerdo de descuento" publicada en gappex.com.ar.';
const FARMACIA_RED =
  'Hasta 40% en medicamentos comprados en farmacias adheridas a la red, según el convenio vigente al momento de la compra.';
const KINE_PCT_PROPIO =
  ' Es un porcentaje propio de esta prestación: no aplica el porcentaje general del plan.';
const SIN_INTERNACION_JOVEN = 'El TyC establece expresamente que no aplica descuento alguno para internación ni práctica quirúrgica.';

export const PLANES: Plan[] = [
  {
    id: 'classic',
    nombre: 'Classic',
    clausula: '6.a',
    tagline: 'Acceso bonificado a la gestión de turnos y estudios. No otorga descuentos.',
    publico: null,
    color: '#03D1E0',
    iconoUrl: 'https://gappex-plan-credential-icons.s3.us-east-1.amazonaws.com/classic.svg',
    tieneDescuentos: false,
    beneficios: {
      consultas: no(SIN_DESCUENTOS),
      'practicas-consultorio': no(SIN_DESCUENTOS),
      laboratorio: no(SIN_DESCUENTOS),
      imagenes: no(SIN_DESCUENTOS),
      kinesiologia: no(SIN_DESCUENTOS),
      guardia: no(SIN_DESCUENTOS),
      interconsultas: no(SIN_DESCUENTOS),
      farmacia: no('El plan bonificado no incluye el beneficio de farmacias adheridas.'),
      habitacion: no(SIN_DESCUENTOS),
      'derechos-quirurgicos': no(SIN_DESCUENTOS),
      'medicamentos-internacion': no(SIN_DESCUENTOS),
      'honorarios-cirujano': no(SIN_DESCUENTOS),
      'honorarios-anestesista': no(SIN_DESCUENTOS),
    },
    requisitos: [],
    exclusiones: [],
    notas: [
      'Es la base de todos los planes pagos: Executive 3000, Premium 3030, Active 7000, Exclusive 8080 y Joven 8000 incluyen su contenido completo y le suman descuentos.',
      'Priority es la excepción: consiste exclusivamente en un servicio de asistencia personalizada.',
    ],
  },
  {
    id: 'executive-3000',
    nombre: 'Executive 3000',
    clausula: '6.b',
    tagline: 'Descuentos ambulatorios del 30%.',
    publico: null,
    color: '#04D1AE',
    iconoUrl: 'https://gappex-plan-credential-icons.s3.us-east-1.amazonaws.com/executive.svg',
    tieneDescuentos: true,
    beneficios: {
      consultas: si(30, '30% en consultas', 'Descuento del 30% sobre el valor particular en consultas ambulatorias.'),
      'practicas-consultorio': si(30, '30% en prácticas', 'Descuento del 30% sobre el valor particular en prácticas ambulatorias. No aplica a prácticas gastroenterológicas.'),
      laboratorio: si(30, '30% en Laboratorio', 'Descuento del 30% sobre el valor particular en estudios ambulatorios de Laboratorio de análisis clínicos.'),
      imagenes: si(30, '30% en Imágenes', 'Descuento del 30% sobre el valor particular en estudios ambulatorios de Medicina por Imágenes.'),
      kinesiologia: si(30, '30% en kinesiología', 'Descuento del 30% sobre el valor particular en sesiones ambulatorias de kinesiología y fisioterapia.'),
      guardia: no(GUARDIA_EXCLUIDA),
      interconsultas: no(INTERCONSULTA_EXCLUIDA),
      farmacia: hasta(40, 'Hasta 40% en farmacias', FARMACIA_RED),
      habitacion: nd(NO_PREVISTO),
      'derechos-quirurgicos': nd(NO_PREVISTO),
      'medicamentos-internacion': nd(NO_PREVISTO),
      'honorarios-cirujano': nd(NO_PREVISTO),
      'honorarios-anestesista': nd(NO_PREVISTO),
    },
    requisitos: [],
    exclusiones: ['Prácticas gastroenterológicas', 'Atenciones de urgencia o guardia', 'Interconsulta especializada'],
    notas: [],
  },
  {
    id: 'premium-3030',
    nombre: 'Premium 3030',
    clausula: '6.c',
    tagline: 'Descuentos ambulatorios y de internación, ambos del 30%.',
    publico: null,
    color: '#264F6B',
    iconoUrl: 'https://gappex-plan-credential-icons.s3.us-east-1.amazonaws.com/premium.svg',
    tieneDescuentos: true,
    beneficios: {
      consultas: si(30, '30% en consultas', 'Descuento del 30% sobre el valor particular en consultas ambulatorias.'),
      'practicas-consultorio': si(30, '30% en prácticas', 'Descuento del 30% sobre el valor particular en prácticas ambulatorias. No aplica a prácticas gastroenterológicas.'),
      laboratorio: si(30, '30% en Laboratorio', 'Descuento del 30% sobre el valor particular en Laboratorio de análisis clínicos, bacteriológicos y virológicos.'),
      imagenes: si(30, '30% en Imágenes', 'Descuento del 30% sobre el valor particular en estudios ambulatorios de Imágenes.'),
      kinesiologia: si(30, '30% en kinesiología', 'Descuento del 30% sobre el valor particular en sesiones ambulatorias de kinesiología y fisioterapia.'),
      guardia: si(30, '30% en guardia', 'Descuento del 30% sobre el valor particular en atenciones de urgencia o guardia.'),
      interconsultas: no(INTERCONSULTA_EXCLUIDA),
      farmacia: hasta(40, 'Hasta 40% en farmacias', FARMACIA_RED),
      habitacion: si(30, '30% en internación', `Descuento del 30% sobre el valor particular en gastos de internación. ${CENTROS_CONVENIO}`),
      'derechos-quirurgicos': si(30, '30% en cirugía', `Descuento del 30% sobre el valor particular en gastos de cirugía. ${CENTROS_CONVENIO}`),
      'medicamentos-internacion': si(30, '30% en medicación', 'Descuento del 30% sobre los medicamentos provistos por el prestador durante la internación.'),
      'honorarios-cirujano': si(30, '30% en honorarios', 'Aplica sólo si el profesional figura en la lista de "profesionales con acuerdo de descuento" publicada en gappex.com.ar.'),
      'honorarios-anestesista': si(30, '30% en honorarios', 'Aplica sólo si el profesional figura en la lista de "profesionales con acuerdo de descuento" publicada en gappex.com.ar.'),
    },
    requisitos: [],
    exclusiones: [
      'Prácticas gastroenterológicas',
      'Tratamientos biológicos y biosimilares',
      'Interconsulta especializada',
      'Alquiler de aparatología para cirugías neurológicas y de ureteroscopio flexible para cirugías urológicas',
      'Medicamentos catastróficos, equinocandinas, Anfotericina B y Voriconazol',
    ],
    notas: [
      'Los descuentos de internación aplican únicamente en centros de salud con convenio vigente con la plataforma.',
      'El acceso a los descuentos de internación queda sujeto a que el prestador tenga disponibilidad de camas, recursos humanos, materiales y técnicos al momento de la demanda.',
    ],
  },
  {
    id: 'active-7000',
    nombre: 'Active 7000',
    clausula: '6.d',
    tagline: 'Descuentos ambulatorios del 70%, para adultos de 31 a 80 años.',
    publico: 'De 31 a 80 años',
    color: '#919187',
    iconoUrl: 'https://gappex-plan-credential-icons.s3.us-east-1.amazonaws.com/active7000.svg',
    tieneDescuentos: true,
    beneficios: {
      consultas: si(70, '70% en consultas', 'Descuento del 70% sobre el valor particular en consultas ambulatorias.'),
      'practicas-consultorio': si(70, '70% en prácticas', 'Descuento del 70% sobre el valor particular en prácticas ambulatorias. No aplica a prácticas gastroenterológicas.'),
      laboratorio: si(70, '70% en Laboratorio', 'Descuento del 70% sobre el valor particular en estudios ambulatorios de Laboratorio de análisis clínicos.'),
      imagenes: si(70, '70% en Imágenes', 'Descuento del 70% sobre el valor particular en estudios ambulatorios de Medicina por Imágenes.'),
      kinesiologia: si(50, '50% en kinesiología', `Descuento del 50% sobre el valor particular en sesiones ambulatorias de kinesiología y fisioterapia.${KINE_PCT_PROPIO}`),
      guardia: no(GUARDIA_EXCLUIDA),
      interconsultas: no(INTERCONSULTA_EXCLUIDA),
      farmacia: hasta(40, 'Hasta 40% en farmacias', FARMACIA_RED),
      habitacion: nd(NO_PREVISTO),
      'derechos-quirurgicos': nd(NO_PREVISTO),
      'medicamentos-internacion': nd(NO_PREVISTO),
      'honorarios-cirujano': nd(NO_PREVISTO),
      'honorarios-anestesista': nd(NO_PREVISTO),
    },
    requisitos: ['Rango etario excluyente: no puede contratarse fuera de los 31 a 80 años (ambos incluidos)'],
    exclusiones: ['Prácticas gastroenterológicas', 'Atenciones de urgencia o guardia', 'Interconsulta especializada'],
    notas: [],
  },
  {
    id: 'priority',
    nombre: 'Priority',
    clausula: '6.e',
    tagline: 'Ejecutivo personal dedicado y turnos prioritarios. No otorga descuentos médicos.',
    publico: 'Cupo limitado',
    color: '#333333',
    iconoUrl: 'https://gappex-plan-credential-icons.s3.us-east-1.amazonaws.com/priority.svg',
    tieneDescuentos: false,
    beneficios: {
      consultas: no(SIN_DESCUENTOS),
      'practicas-consultorio': no(SIN_DESCUENTOS),
      laboratorio: no(SIN_DESCUENTOS),
      imagenes: no(SIN_DESCUENTOS),
      kinesiologia: no(SIN_DESCUENTOS),
      guardia: no(SIN_DESCUENTOS),
      interconsultas: no(SIN_DESCUENTOS),
      farmacia: hasta(40, 'Hasta 40% en farmacias', FARMACIA_RED),
      habitacion: no(SIN_DESCUENTOS),
      'derechos-quirurgicos': no(SIN_DESCUENTOS),
      'medicamentos-internacion': no(SIN_DESCUENTOS),
      'honorarios-cirujano': no(SIN_DESCUENTOS),
      'honorarios-anestesista': no(SIN_DESCUENTOS),
    },
    requisitos: ['Cantidad limitada de beneficiarios: la plataforma se reserva el derecho de admisión'],
    exclusiones: [],
    notas: [
      'Es un servicio de gestión personalizada, no un plan de descuentos: no reduce el costo de las prestaciones médicas.',
      'Incluye un ejecutivo coordinador asignado, gestión inmediata y prioritaria de turnos, coordinación de turnos múltiples y derivaciones, con atención telefónica de 8 a 20 hs los días hábiles.',
    ],
  },
  {
    id: 'exclusive-8080',
    nombre: 'Exclusive 8080',
    clausula: '6.f',
    tagline: 'El más completo: 80% ambulatorio y de internación, con seguro de vida incluido.',
    publico: 'Hasta 80 años',
    color: '#CEBE9D',
    iconoUrl: 'https://gappex-plan-credential-icons.s3.us-east-1.amazonaws.com/exclusive8080.svg',
    tieneDescuentos: true,
    beneficios: {
      consultas: si(80, '80% en consultas', `Descuento del 80% sobre el valor particular en consultas. ${CENTROS_CONVENIO}`),
      'practicas-consultorio': si(80, '80% en prácticas', `Descuento del 80% sobre el valor particular en prácticas ambulatorias. ${CENTROS_CONVENIO}`),
      laboratorio: si(80, '80% en Laboratorio', `Descuento del 80% sobre el valor particular en Laboratorio de análisis clínicos. ${CENTROS_CONVENIO}`),
      imagenes: si(80, '80% en Imágenes', `Descuento del 80% sobre el valor particular en Medicina por Imágenes. ${CENTROS_CONVENIO}`),
      kinesiologia: si(50, '50% en kinesiología', `Descuento del 50% sobre el valor particular en sesiones ambulatorias de kinesiología y fisioterapia.${KINE_PCT_PROPIO}`),
      guardia: si(80, '80% en guardia', 'Descuento del 80% sobre el valor particular en atenciones de urgencia o guardia.'),
      interconsultas: no('El TyC excluye expresamente la interconsulta especializada durante la internación.'),
      farmacia: hasta(40, 'Hasta 40% en farmacias', FARMACIA_RED),
      habitacion: si(
        80,
        '80% en internación',
        `Descuento del 80% sobre el valor particular de "pensión" o "día cama", en sala común, terapia intensiva y maternidad. Tope de 30 días de internación cada 365 días. ${CENTROS_CONVENIO}`,
      ),
      'derechos-quirurgicos': si(80, '80% en cirugía', `Descuento del 80% en gastos quirúrgicos. ${CENTROS_CONVENIO}`),
      'medicamentos-internacion': si(
        50,
        '50% en medicación',
        'Descuento del 50% en consumos y gastos de farmacia, sólo por provisión de la farmacia interna de la institución donde el beneficiario está internado. Excluye prótesis, medicamentos catastróficos, equinocandinas, Anfotericina B y Voriconazol.',
      ),
      'honorarios-cirujano': si(80, '80% en honorarios', 'Aplica sólo si el profesional figura en la lista de "profesionales con acuerdo de descuento" publicada en gappex.com.ar.'),
      'honorarios-anestesista': si(80, '80% en honorarios', 'Aplica sólo si el profesional figura en la lista de "profesionales con acuerdo de descuento" publicada en gappex.com.ar.'),
    },
    requisitos: [
      'Declaración jurada de preexistencias',
      'Análisis médicos con profesionales designados por la plataforma, a costo de la plataforma',
      'Gappex se reserva el derecho de admisión según el resultado de los estudios',
    ],
    exclusiones: [
      'Internaciones originadas en enfermedad preexistente dentro de los 12 meses de aceptado el ingreso (no aplica en caso de accidente)',
      'Prótesis, medicamentos catastróficos, equinocandinas, Anfotericina B y Voriconazol',
      'Alquiler de aparatología para cirugías neurológicas y de ureteroscopio flexible para cirugías urológicas',
      'Tratamientos biológicos y biosimilares',
      'Internación domiciliaria, enfermería domiciliaria y acompañantes terapéuticos',
      'Traslados, movilidad o derivaciones a otras instituciones',
    ],
    notas: [
      'Pediatría: los descuentos aplican solamente en atención de primer nivel (consultorio, imágenes y laboratorio). No aplican en segundo nivel (cirugías, internaciones) ni tercer nivel (terapia intensiva).',
      'El acceso a los descuentos de internación queda sujeto a que el prestador tenga disponibilidad de camas, recursos humanos, materiales y técnicos al momento de la demanda.',
      'Carencia de 12 meses por enfermedad preexistente, no aplicable en caso de accidente.',
    ],
  },
  {
    id: 'joven-8000',
    nombre: 'Joven 8000',
    clausula: '6.g',
    tagline: 'Descuentos ambulatorios del 80%, para jóvenes de 18 a 30 años.',
    publico: 'De 18 a 30 años',
    color: '#B24BC5',
    iconoUrl: 'https://gappex-plan-credential-icons.s3.us-east-1.amazonaws.com/planjoven.svg',
    tieneDescuentos: true,
    beneficios: {
      consultas: si(80, '80% en consultas', 'Descuento del 80% sobre el valor particular en consultas ambulatorias.'),
      'practicas-consultorio': si(80, '80% en prácticas', 'Descuento del 80% sobre el valor particular en prácticas ambulatorias. No aplica a prácticas gastroenterológicas.'),
      laboratorio: si(80, '80% en Laboratorio', 'Descuento del 80% sobre el valor particular en estudios ambulatorios de Laboratorio de análisis clínicos.'),
      imagenes: si(80, '80% en Imágenes', 'Descuento del 80% sobre el valor particular en estudios ambulatorios de Medicina por Imágenes.'),
      kinesiologia: si(50, '50% en kinesiología', `Descuento del 50% sobre el valor particular en sesiones ambulatorias de kinesiología y fisioterapia.${KINE_PCT_PROPIO}`),
      guardia: nd(NO_PREVISTO),
      interconsultas: no(INTERCONSULTA_EXCLUIDA),
      farmacia: hasta(40, 'Hasta 40% en farmacias', FARMACIA_RED),
      habitacion: no(SIN_INTERNACION_JOVEN),
      'derechos-quirurgicos': no(SIN_INTERNACION_JOVEN),
      'medicamentos-internacion': no(SIN_INTERNACION_JOVEN),
      'honorarios-cirujano': no(SIN_INTERNACION_JOVEN),
      'honorarios-anestesista': no(SIN_INTERNACION_JOVEN),
    },
    requisitos: ['Rango etario excluyente: no puede contratarse fuera de los 18 a 30 años (ambos incluidos)'],
    exclusiones: [
      'Prácticas gastroenterológicas',
      'Interconsulta especializada',
      'Internación y práctica quirúrgica de cualquier tipo',
      'Consultas y tratamientos relacionados con implantes, motivos estéticos y prácticas bariátricas',
    ],
    notas: [
      'Los beneficios son personales: no aplican a terceros, cualquiera sea el vínculo con el usuario.',
      'Al cumplir 31 años el usuario es migrado automáticamente al Plan Classic y debe gestionar por su cuenta un nuevo plan acorde a su rango etario.',
      'La deuda de un solo canon mensual da de baja la suscripción de forma automática, sin intimación ni aviso previo.',
    ],
  },
];

/** Prestaciones sin descuento en NINGÚN plan. */
export const EXCLUSIONES_COMUNES: string[] = [
  'Odontología',
  'Prótesis e implantes',
  'Tratamientos experimentales o no autorizados por ANMAT, y medicamentos catastróficos',
  'Tratamientos o intervenciones estéticas de cualquier índole',
  'Tratamientos oncológicos',
  'Tratamientos psiquiátricos o psicológicos',
  'Cirugías bariátricas y afines (bypass gástrico, manga gástrica, balón intragástrico)',
  'Tratamientos de fertilidad, esterilidad y procedimientos anticonceptivos',
  'Prestaciones realizadas fuera de la red de prestadores adheridos',
];

/** Aclaraciones que aplican a todos los planes. */
export const NOTAS_GENERALES: string[] = [
  'Todos los descuentos se calculan sobre el "valor particular": el precio estándar que fija el prestador para un paciente sin ningún tipo de cobertura ni beneficio. Ese valor incluye IVA.',
  'Los descuentos aplican exclusivamente dentro de la red de prestadores adheridos. Las prestaciones realizadas fuera de la red no tienen beneficio.',
  'El descuento aplica sólo al titular de la suscripción, salvo en los planes grupales.',
  'Ningún plan admite mayores de 80 años: el TyC establece que no pueden acceder a ningún plan ni beneficio otorgado por Gappex.',
  'Gappex es un servicio de gestión e intermediación de turnos. No es una entidad de medicina prepaga, obra social ni empresa de seguros, y no presta servicios médicos ni de urgencia de ningún tipo.',
  'Los beneficios se interpretan con criterio restrictivo: si una prestación no está expresamente contemplada en el plan, no tiene descuento.',
  'Además de las exclusiones de cada plan, el TyC excluye los descuentos cuando la prestación deriva de autolesión, consumo de sustancias o alcohol, accidentes de trabajo (Ley 24.557), deportes peligrosos, terrorismo o guerra, catástrofes naturales, epidemias y prácticas penadas por ley.',
  'Los planes y sus beneficios pueden modificarse. Todo cambio se notifica al titular en su domicilio electrónico con 15 días corridos de anticipación.',
];
